<script>
import DataLayer from '@services/data-layer/index'
import modalService from '@/services/modal-service'
import ShowService from '@/services/show-service'

export default {
    props: {
        action: {
            type: Object,
            default: () => {},
        },
        title: {
            type: String,
            default: 'Learn more...',
        },
    },
    data() {
        return {
            playlist: {},
            playlistMixes: [],
            playlistLoaded: false,
            totalMixes: null,
            limit: 20,
        }
    },
    computed: {
        loggedIn() {
            return this.$store.getters['auth/loggedIn']
        },
        isChannelPlaying() {
            if (!this.action || this.action.type !== 'playChannel') return false
            const { playback } = this.$store.state
            return (
                (playback.status === 'playing' || playback.status === 'loading') &&
                playback.resource &&
                playback.resource.type === 'channel' &&
                playback.resource.id === this.action.value
            )
        },
        isMixPlaying() {
            if (!this.action || this.action.type !== 'playMix') return false
            const { playback } = this.$store.state
            return (
                (playback.status === 'playing' || playback.status === 'loading') &&
                playback.resource &&
                playback.resource.type === 'mix' &&
                playback.resource.id === this.action.value
            )
        },
        isMixLoaded() {
            if (!this.action || this.action.type !== 'playMix') return false
            const playbackResource = this.$store.state.playback.resource
            return playbackResource && this.action.value === playbackResource.id
        },
        isCurrentPlaylistPlaying() {
            if (this.action && this.action.type && this.action.type === 'playPlaylist')
                return Number(this.action.value) === this.$store.state.queue.playlistId
            return false
        },
    },
    mounted() {
        this.action && this.action.type && this.action.type === 'playPlaylist' && this.getPlaylist(this.action.value)
    },
    methods: {
        handleChannelClick() {
            if (this.isChannelPlaying) {
                this.$queueService.pause()
                return
            }
            if (!this.loggedIn) {
                modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
                return
            }
            this.$queueService.playRadioChannel(this.action.value).catch(e => {
                this.$store.dispatch('appearance/handleNotification', { message: e.message, type: 'warning' })
            })
        },
        handleMixClick() {
            if (this.isMixLoaded) {
                this.$queueService.pause()
                return
            }
            if (this.isMixPlaying) {
                this.$queueService.pause()
                return
            }
            if (!this.loggedIn) {
                modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
                return
            }
            DataLayer.mixes.getById(this.action.value).then(({ body: mix }) => {
                this.$queueService.playMix(mix).catch(e => {
                    this.$store.dispatch('appearance/handleNotification', { message: e.message, type: 'warning' })
                })
            })
        },
        increaseReach(id) {
            ShowService().increaseSessionReach(id)
            this.$store.dispatch('shows/increaseReach')
        },
        getPlaylist(playlistId) {
            DataLayer.playlists.getById(playlistId).then(playlist => {
                this.playlist = playlist.body
            })
        },
        getPlaylistMixesAndPlay({ playlistId = this.action.value, limit = this.limit, offset = 0 }) {
            if (this.totalMixes === this.playlistMixes.length) {
                this.playPlaylist()
                return
            }
            DataLayer.playlists.getRevisionsMixes(playlistId, limit, offset).then(mixes => {
                this.totalMixes = Number(mixes.headers['x-items-total'])
                this.playlistMixes = mixes.body.map(revisionsMixes => revisionsMixes.mixes)
                this.getPlaylistMixesAndPlay({ limit: this.totalMixes })
            })
        },
        playPlaylist() {
            if (this.isCurrentPlaylistPlaying) {
                this.$queueService.pause()
            } else {
                this.$queueService.playMixMultiple({
                    mixes: [...this.playlistMixes],
                    id: this.playlist.id,
                    position: 0,
                })
            }
        },
    },
}
</script>

<template>
    <div v-if="action">
        <button
            v-if="action.type === 'playChannel'"
            class="btn"
            :title="isChannelPlaying ? 'Pause' : 'Listen Now'"
            :aria-label="isChannelPlaying ? 'Pause' : 'Listen Now'"
            @click="handleChannelClick"
        >{{ isChannelPlaying ? 'Pause' : 'Listen Now' }}</button>
        <button
            v-if="action.type === 'playMix'"
            class="btn"
            :title="isMixPlaying ? 'Pause' : 'Listen Now'"
            :aria-label="isMixPlaying ? 'Pause' : 'Listen Now'"
            @click="handleMixClick"
        >{{ isMixPlaying ? 'Pause' : 'Listen Now' }}</button>
        <button
            v-if="action.type === 'playPlaylist'"
            class="btn"
            :title="isCurrentPlaylistPlaying ? 'Pause' : 'Listen Now'"
            :aria-label="isCurrentPlaylistPlaying ? 'Pause' : 'Listen Now'"
            @click="getPlaylistMixesAndPlay({})"
        >{{ isCurrentPlaylistPlaying ? 'Pause' : 'Listen Now' }}</button>

        <BaseLink
            v-if="action.type === 'navigateInternal'"
            :title="title"
            :aria-label="title"
            :to="{ path: action.value }"
        >
            <button
                class="btn"
                :title="title"
                :aria-label="title"
            >Learn More</button>
        </BaseLink>
        <BaseLink
            v-if="action.type === 'navigateExternal'"
            :href="action.value"
        >
            <button
                class="btn"
                :title="title"
                :aria-label="title"
            >Learn More</button>
        </BaseLink>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: $s-xs $s-m;
    background-color: $c-white;
    color: $c-black;
    border: none;
    border-radius: 20px;
    font-family: $system-default-font-family;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.85;
    }
}
</style>
